import React, { useEffect, useMemo, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { api, BACKEND_URL, downloadWithAuth, getToken, isSpendApprovalError } from './api';
import { toast } from 'sonner';
import { copyText } from './clipboard';
import { errorMessage } from './errors';
import { useAuth } from './AuthContext';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import PhoneInput from './shared/PhoneInput';
import NumberInput from './shared/NumberInput';
import PickSelect from './shared/PickSelect';
import { formatDate, formatDateTime } from './kit/time';
import {
  ShieldCheck, CheckCircle2, Circle, FileText, Calendar as CalendarIcon,
  Loader2, X, Sparkles, AlertTriangle, ExternalLink, Download,
  Scale, Send, MessageSquare, Copy,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Colours drawn from the Portal palette (deep navy + broker green)
// ---------------------------------------------------------------------------
const C = {
  navy: '#070B5F', green: '#6BA82E', lime: '#A6E56C',
  amber: '#f59e0b', red: '#dc2626', slate: '#64748b',
};

// Shown on a doc or event only when the page holds both products (a dual-licensed workspace).
const PRODUCT_LABEL = { mortgage: 'Mortgage', real_estate: 'Real estate' };

const gradeColor = (grade) => ({
  A: C.green, B: '#65a30d', C: C.amber, D: '#ea580c', F: C.red,
}[grade] || C.slate);

// ---------------------------------------------------------------------------
// Wizard field spec — grouped into 3 steps so it isn't an intimidating wall
// ---------------------------------------------------------------------------
const WIZARD_STEPS = [
  {
    title: 'Company identity',
    fields: [
      { key: 'company_name', label: 'Legal company name', required: true },
      { key: 'legal_entity', label: 'Entity type', type: 'select',
        options: ['LLC', 'Corporation', 'Sole Proprietorship', 'Partnership'] },
      { key: 'nmls_id', label: 'NMLS ID', required: true, placeholder: 'e.g. 1234567' },
      { key: 'broker_license', label: 'State broker/branch license #' },
      { key: 'ein', label: 'EIN (Tax ID)' },
      { key: 'year_founded', label: 'Year founded', type: 'number' },
    ],
  },
  {
    title: 'Broker & compliance officer',
    fields: [
      { key: 'broker_name', label: 'Broker name', required: true },
      { key: 'broker_email', label: 'Broker email', type: 'email', required: true },
      { key: 'broker_phone', label: 'Broker phone', type: 'tel' },
      { key: 'compliance_officer_name', label: 'Designated compliance officer', required: true },
      { key: 'team_size', label: 'Number of licensed staff', type: 'number' },
    ],
  },
  {
    title: 'Operations',
    fields: [
      { key: 'office_address', label: 'Primary office address' },
      { key: 'trust_account_bank', label: 'Escrow / trust account bank',
        hint: 'Real estate: sales earnest money escrow. Mortgage: leave blank if you do not custody funds.' },
      { key: 'website', label: 'Website URL', type: 'url' },
      { key: 'state', label: 'Primary state', type: 'select',
        options: ['TX', 'LA', 'OK', 'FL', 'CA', 'NY', 'IL', 'GA', 'AZ',
                  'NC', 'WA', 'MI', 'OH', 'CO', 'NJ', 'MD'],
        hint: '16 states supported. More coming as customers request them.' },
      { key: 'additional_notes', label: 'Anything else we should know?', type: 'textarea' },
    ],
  },
];

// ===========================================================================
// Main page
// ===========================================================================
export default function BrokerCompliancePage() {
  const { user } = useAuth();
  // Same rule as the server: the wizard and the attorney send/settings routes refuse non-admin seats.
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [docs, setDocs] = useState([]);
  const [events, setEvents] = useState([]);
  const [wizardAnswers, setWizardAnswers] = useState({});
  const [wizardOpen, setWizardOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [attorneyOpen, setAttorneyOpen] = useState(false);
  const [attorneySettings, setAttorneySettings] = useState({ attorney_email: '', attorney_name: '' });
  const [reviews, setReviews] = useState([]);
  const [statesInfo, setStatesInfo] = useState({ supported: ['TX'], active: 'TX' });

  const refresh = async () => {
    try {
      const [h, d, c, w, s, st] = await Promise.all([
        api.get('/broker-kit/health-score'),
        api.get('/broker-kit/docs'),
        api.get('/broker-kit/calendar'),
        api.get('/broker-kit/setup-wizard'),
        api.get('/broker-kit/attorney/settings'),
        api.get('/broker-kit/states'),
      ]);
      setHealth(h.data);
      setDocs(d.data.docs);
      setEvents(c.data.events);
      setWizardAnswers(w.data.answers || {});
      setAttorneySettings(s.data);
      setStatesInfo(st.data);
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to load compliance data'));
    } finally {
      setLoading(false);
    }
  };

  // Attorney reviews are admin-only (403 for other seats). Fetched on their
  // own so that refusal never breaks the rest of the page.
  const loadReviews = async () => {
    try {
      const r = await api.get('/broker-kit/attorney/reviews');
      setReviews(r.data.reviews || []);
    } catch (e) {
      setReviews([]);
      if (e?.response?.status !== 403) toast.error(errorMessage(e, 'Failed to load attorney reviews'));
    }
  };

  useEffect(() => { refresh(); loadReviews(); }, []);

  const toggleDoc = async (doc) => {
    try {
      await api.post(`/broker-kit/docs/${doc.id}/complete`, { complete: !doc.completed });
      toast.success(doc.completed ? 'Marked as pending' : 'Marked complete');
      refresh();
    } catch (e) {
      toast.error(errorMessage(e, 'Update failed'));
    }
  };

  const toggleEvent = async (evt) => {
    try {
      await api.post(`/broker-kit/calendar/${evt.id}/ack`, {
        next_due: evt.next_due,
        acknowledged: !evt.acknowledged,
      });
      toast.success(evt.acknowledged ? 'Acknowledgement removed' : 'Acknowledged');
      refresh();
    } catch (e) {
      toast.error(errorMessage(e, 'Update failed'));
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-slate-500 flex items-center gap-2">
        <Loader2 className="animate-spin" size={16} /> Loading compliance kit…
      </div>
    );
  }

  const showProduct = new Set([...docs, ...events].map((i) => i.product_type)).size > 1;

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="broker-compliance-page">
      {/* Header */}
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold flex items-center gap-2" style={{ color: C.navy }}>
            <ShieldCheck /> Broker Compliance Kit
          </h1>
          <p className="text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>Texas-ready policies, procedures and calendar. Health-scored in real time.</span>
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded border"
              style={{ background: 'white', borderColor: C.navy, color: C.navy }}
              data-testid="active-state-chip"
              title={`Kits available: ${statesInfo.supported.join(', ')}`}
            >
              State: {statesInfo.active}
            </span>
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setAttorneyOpen(true)}
              data-testid="btn-open-attorney"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-md font-medium text-sm border transition hover:bg-slate-50"
              style={{ borderColor: C.navy, color: C.navy }}
            >
              <Scale size={14} /> Send to Attorney
            </button>
            <button
              onClick={() => setWizardOpen(true)}
              data-testid="btn-open-wizard"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-md text-white font-medium text-sm shadow-sm hover:opacity-90 transition"
              style={{ background: C.navy }}
            >
              <Sparkles size={14} /> {Object.keys(wizardAnswers).length ? 'Update Setup Wizard' : 'Start Setup Wizard'}
            </button>
          </div>
        )}
      </div>

      {/* Health Score */}
      <HealthScoreCard health={health} />

      {/* Documents */}
      <section className="mt-8">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-serif text-xl font-semibold" style={{ color: C.navy }}>
            Policy &amp; Procedure Templates
          </h2>
          <span className="text-xs text-slate-500" data-testid="docs-progress">
            {health?.breakdown.docs.completed} / {health?.breakdown.docs.total} complete
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {docs.map((d) => (
            <DocCard key={d.id} doc={d} showProduct={showProduct} onToggle={() => toggleDoc(d)} onView={() => setViewingDoc(d)} />
          ))}
        </div>
      </section>

      {/* Calendar */}
      <section className="mt-8">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-serif text-xl font-semibold" style={{ color: C.navy }}>
            Compliance Calendar
          </h2>
          <span className="text-xs text-slate-500" data-testid="events-progress">
            {health?.breakdown.calendar.acknowledged} / {health?.breakdown.calendar.total} acknowledged
          </span>
        </div>
        <p className="text-xs text-slate-500 -mt-1 mb-3" data-testid="reminders-note">
          Admins get a bell notification and an email before each due date. Acknowledge an event to stop its reminders.
        </p>
        <div className="space-y-2">
          {events.length === 0 && (
            <div className="text-sm text-slate-500 py-6 text-center bg-white rounded-xl border border-slate-100">
              No upcoming compliance events.
            </div>
          )}
          {events.map((e) => <EventRow key={`${e.id}:${e.next_due}`} event={e} showProduct={showProduct} onToggle={() => toggleEvent(e)} />)}
        </div>
      </section>

      {/* Attorney Review Inbox */}
      {reviews.length > 0 && (
        <section className="mt-8" data-testid="attorney-reviews-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-serif text-xl font-semibold flex items-center gap-2" style={{ color: C.navy }}>
              <Scale size={18} /> Attorney Review Inbox
            </h2>
            <span className="text-xs text-slate-500">{reviews.length} session{reviews.length === 1 ? '' : 's'}</span>
          </div>
          <div className="space-y-2">
            {reviews.map((r) => <ReviewRow key={r.review_id} review={r} />)}
          </div>
        </section>
      )}

      {viewingDoc && <DocViewerModal doc={viewingDoc} onClose={() => setViewingDoc(null)} />}
      {wizardOpen && (
        <SetupWizardModal
          initial={wizardAnswers}
          onClose={() => setWizardOpen(false)}
          onSaved={() => { setWizardOpen(false); refresh(); }}
        />
      )}
      {attorneyOpen && (
        <AttorneyModal
          settings={attorneySettings}
          docs={docs}
          onClose={() => setAttorneyOpen(false)}
          onSent={() => { setAttorneyOpen(false); refresh(); loadReviews(); }}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Health Score
// ===========================================================================
function HealthScoreCard({ health }) {
  if (!health) return null;
  const { score, grade, label, breakdown } = health;
  const color = gradeColor(grade);
  // SVG dashoffset for a circle radius 54 → circumference 339.29
  const CIRC = 2 * Math.PI * 54;
  const dash = CIRC * (1 - score / 100);

  return (
    <div
      className="rounded-2xl p-6 shadow-sm border border-slate-100 grid md:grid-cols-[auto_1fr] gap-6 items-center"
      style={{ background: 'white' }}
      data-testid="health-score-card"
    >
      <div className="relative w-[140px] h-[140px] mx-auto">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r="54" stroke="#e2e8f0" strokeWidth="10" fill="none" />
          <circle
            cx="60" cy="60" r="54"
            stroke={color} strokeWidth="10" fill="none"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={dash}
            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-serif text-4xl font-bold" style={{ color: C.navy }} data-testid="health-score-value">{score}</div>
          <div className="text-xs uppercase tracking-wider font-semibold" style={{ color }}>Grade {grade}</div>
        </div>
      </div>
      <div>
        <div className="text-sm text-slate-500">Compliance Health Score</div>
        <div className="font-serif text-2xl font-semibold mt-1" style={{ color }}>{label}</div>
        <div className="text-xs text-slate-500 mt-1 max-w-md">
          Score = 40 (docs) + 30 (wizard) + 30 (calendar). Complete more items below to raise it.
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 max-w-xl">
          <ScoreBar label="Docs" pts={breakdown.docs.points} max={breakdown.docs.max}
                   sub={`${breakdown.docs.completed}/${breakdown.docs.total}`} color={C.green} testId="score-docs" />
          <ScoreBar label="Wizard" pts={breakdown.wizard.points} max={breakdown.wizard.max}
                   sub={breakdown.wizard.complete ? 'Complete' : `${breakdown.wizard.filled}/${breakdown.wizard.required}`} color={C.navy} testId="score-wizard" />
          <ScoreBar label="Calendar" pts={breakdown.calendar.points} max={breakdown.calendar.max}
                   sub={`${breakdown.calendar.acknowledged}/${breakdown.calendar.total}`} color={C.amber} testId="score-calendar" />
        </div>
      </div>
    </div>
  );
}

function ScoreBar({ label, pts, max, sub, color, testId }) {
  const pct = Math.min(100, (pts / max) * 100);
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</span>
        <span className="text-xs font-mono text-slate-500">{pts}/{max}</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color, transition: 'width 0.6s ease' }} />
      </div>
      <div className="text-[11px] text-slate-500 mt-1">{sub}</div>
    </div>
  );
}

// ===========================================================================
// Doc card + viewer
// ===========================================================================
function ProductChip({ product, testId }) {
  return (
    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded border border-slate-200 text-slate-500 shrink-0" data-testid={testId}>
      {PRODUCT_LABEL[product] || product}
    </span>
  );
}

function DocCard({ doc, showProduct, onToggle, onView }) {
  const catBadge = {
    policy: { bg: '#dbeafe', fg: '#1e40af', label: 'Policy' },
    procedure: { bg: '#fef3c7', fg: '#92400e', label: 'Procedure' },
    contract: { bg: '#fce7f3', fg: '#9d174d', label: 'Contract' },
    disclosure: { bg: '#e0e7ff', fg: '#3730a3', label: 'Disclosure' },
  }[doc.category] || { bg: '#f1f5f9', fg: '#334155', label: doc.category };

  return (
    <div
      className="bg-white rounded-xl border border-slate-100 p-4 flex gap-3 hover:shadow-md transition"
      data-testid={`doc-card-${doc.id}`}
    >
      <button
        onClick={onToggle}
        data-testid={`doc-toggle-${doc.id}`}
        title={doc.completed ? 'Mark as pending' : 'Mark complete'}
        className="shrink-0 pt-0.5"
      >
        {doc.completed
          ? <CheckCircle2 size={20} style={{ color: C.green }} />
          : <Circle size={20} className="text-slate-300 hover:text-slate-400" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <button
            onClick={onView}
            data-testid={`doc-view-${doc.id}`}
            className="text-sm font-semibold text-left hover:underline"
            style={{ color: C.navy }}
          >
            {doc.title}
          </button>
          <div className="flex items-center gap-1 shrink-0">
            {showProduct && <ProductChip product={doc.product_type} testId={`doc-product-${doc.id}`} />}
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded"
                  style={{ background: catBadge.bg, color: catBadge.fg }}>
              {catBadge.label}
            </span>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{doc.summary}</p>
        <div className="text-[11px] text-slate-400 mt-1 font-mono">{doc.required_by}</div>
      </div>
    </div>
  );
}

function DocViewerModal({ doc, onClose }) {
  const [filled, setFilled] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/broker-kit/docs/${doc.id}`, { params: { fill: 1 } })
      .then(({ data }) => setFilled(data))
      .catch((e) => toast.error(errorMessage(e, 'Load failed')))
      .finally(() => setLoading(false));
  }, [doc.id]);

  const downloadTxt = () => {
    if (!filled?.body) return;
    const blob = new Blob([filled.body], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${doc.id}.md`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        showClose={false}
        className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
        data-testid="doc-viewer-modal"
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <div className="text-xs text-slate-500 font-mono">{doc.required_by}</div>
            <DialogPrimitive.Title asChild>
              <h3 className="font-serif text-lg font-semibold" style={{ color: C.navy }}>{doc.title}</h3>
            </DialogPrimitive.Title>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadTxt}
              disabled={!filled}
              data-testid="doc-download"
              className="inline-flex items-center gap-1 text-xs px-3 h-8 rounded-md border border-slate-200 hover:bg-slate-50"
            >
              <Download size={12} /> Download
            </button>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="doc-close">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto text-sm text-slate-700">
          {loading && <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={14} /> Loading template…</div>}
          {filled && (
            <pre className="whitespace-pre-wrap font-sans leading-relaxed">{filled.body}</pre>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Event row
// ===========================================================================
function EventRow({ event, showProduct, onToggle }) {
  const overdue = event.days_away < 0;
  const active = event.is_active_reminder;
  const border = overdue ? '#dc2626' : (active ? '#f59e0b' : '#e2e8f0');
  return (
    <div
      className="bg-white rounded-xl border p-4 flex items-center gap-3"
      style={{ borderColor: border }}
      data-testid={`event-row-${event.id}`}
    >
      <button onClick={onToggle} data-testid={`event-ack-${event.id}`} className="shrink-0" title={event.acknowledged ? 'Undo acknowledge' : 'Acknowledge'}>
        {event.acknowledged
          ? <CheckCircle2 size={20} style={{ color: C.green }} />
          : <Circle size={20} className="text-slate-300 hover:text-slate-400" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold" style={{ color: C.navy }}>{event.title}</span>
          {showProduct && <ProductChip product={event.product_type} testId={`event-product-${event.id}`} />}
          {overdue && (
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ background: '#fee2e2', color: '#991b1b' }}>
              Overdue
            </span>
          )}
          {!overdue && active && (
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded inline-flex items-center gap-1" style={{ background: '#fef3c7', color: '#92400e' }}>
              <AlertTriangle size={10} /> Due soon
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{event.description}</p>
      </div>
      <div className="text-right shrink-0">
        <div className="text-xs font-mono text-slate-500">{event.next_due}</div>
        <div className="text-xs font-semibold" style={{ color: overdue ? C.red : (active ? C.amber : C.slate) }}>
          {overdue ? `${-event.days_away}d overdue` : `${event.days_away}d away`}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Setup Wizard
// ===========================================================================
function SetupWizardModal({ initial, onClose, onSaved }) {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState(() => ({ state: 'TX', ...initial }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const current = WIZARD_STEPS[step];
  const isLast = step === WIZARD_STEPS.length - 1;

  const submit = async () => {
    setSaving(true);
    try {
      // Coerce numeric fields
      const payload = { ...values };
      ['team_size', 'year_founded', 'fiscal_year_start_month'].forEach((k) => {
        if (payload[k] != null && payload[k] !== '') payload[k] = Number(payload[k]);
      });
      await api.post('/broker-kit/setup-wizard', payload);
      toast.success('Setup wizard saved — docs will auto-fill');
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        showClose={false}
        className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
        data-testid="wizard-modal"
      >
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Step {step + 1} of {WIZARD_STEPS.length}
            </div>
            <DialogPrimitive.Title asChild>
              <h3 className="font-serif text-lg font-semibold" style={{ color: C.navy }}>{current.title}</h3>
            </DialogPrimitive.Title>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="wizard-close">
            <X size={18} />
          </button>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div className="h-full" style={{ width: `${((step + 1) / WIZARD_STEPS.length) * 100}%`, background: C.green, transition: 'width 0.3s ease' }} />
        </div>

        <div className="p-6 overflow-y-auto space-y-4">
          {current.fields.map((f) => (
            <FieldInput key={f.key} field={f} value={values[f.key] ?? ''} onChange={(v) => set(f.key, v)} />
          ))}
        </div>

        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="text-sm px-4 h-9 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
            data-testid="wizard-back"
          >
            Back
          </button>
          {!isLast ? (
            <button
              onClick={() => setStep((s) => s + 1)}
              className="text-sm px-4 h-9 rounded-md text-white font-medium"
              style={{ background: C.navy }}
              data-testid="wizard-next"
            >
              Continue
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={saving}
              className="text-sm px-4 h-9 rounded-md text-white font-medium inline-flex items-center gap-2 disabled:opacity-60"
              style={{ background: C.green }}
              data-testid="wizard-submit"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
              Save &amp; finish
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FieldInput({ field, value, onChange }) {
  const commonProps = {
    id: field.key,
    'data-testid': `wizard-field-${field.key}`,
    value: value,
    onChange: (e) => onChange(e.target.value),
    placeholder: field.placeholder || '',
    className: 'w-full h-10 rounded-md border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6BA82E]/40',
  };
  return (
    <div>
      {field.type !== 'select' && (
        <label htmlFor={field.key} className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </label>
      )}
      {field.type === 'select' ? (
        <PickSelect
          label={field.required ? `${field.label} *` : field.label}
          labelClassName="block text-xs font-semibold uppercase tracking-wider text-slate-600"
          value={value}
          onChange={onChange}
          options={field.options.map((o) => ({ value: o, label: o }))}
          placeholder="Select…"
          testId={`wizard-field-${field.key}`}
        />
      ) : field.type === 'textarea' ? (
        <textarea {...commonProps} rows={3} className={commonProps.className + ' h-auto py-2'} />
      ) : field.type === 'tel' ? (
        <PhoneInput id={field.key} data-testid={`wizard-field-${field.key}`} value={value} onChange={onChange} className={commonProps.className} />
      ) : field.type === 'number' ? (
        <NumberInput integer id={field.key} data-testid={`wizard-field-${field.key}`} value={value} onChange={onChange} className={commonProps.className} />
      ) : (
        <Input {...commonProps} className="h-10" type={field.type || 'text'} />
      )}
      {field.hint && <div className="text-[11px] text-slate-400 mt-1">{field.hint}</div>}
    </div>
  );
}

// ===========================================================================
// Attorney Review — inbox row + send modal
// ===========================================================================
const STATUS_STYLE = {
  sent:              { bg: '#eff6ff', fg: '#1e40af', label: 'Sent · awaiting attorney' },
  opened:            { bg: '#fef3c7', fg: '#92400e', label: 'Opened' },
  commented:         { bg: '#e0e7ff', fg: '#3730a3', label: 'Comments received' },
  approved:          { bg: '#dcfce7', fg: '#166534', label: 'Approved' },
  changes_requested: { bg: '#fee2e2', fg: '#991b1b', label: 'Changes requested' },
};

function ReviewRow({ review }) {
  const [expanded, setExpanded] = useState(false);
  const [full, setFull] = useState(null);
  const style = STATUS_STYLE[review.status] || STATUS_STYLE.sent;

  const loadFull = async () => {
    if (full) { setExpanded(!expanded); return; }
    try {
      const { data } = await api.get(`/broker-kit/attorney/reviews/${review.review_id}`);
      setFull(data);
      setExpanded(true);
    } catch (e) {
      toast.error(errorMessage(e, 'Load failed'));
    }
  };

  // The list carries no token; the detail returns it (decrypted) for admins.
  const linkFor = (data) => {
    if (!data?.token) throw new Error('This review has no link on file. Send it again to make a new one.');
    return `${window.location.origin}/attorney/review/${data.token}`;
  };

  const copyLink = async () => {
    if (full) {
      let url;
      try { url = linkFor(full); } catch (e) { toast.error(e.message); return; }
      if (await copyText(url)) toast.success('Review link copied');
      else toast.error('Copy failed — select the text and copy it manually');
      return;
    }
    const load = api.get(`/broker-kit/attorney/reviews/${review.review_id}`).then(({ data }) => {
      setFull(data);
      return linkFor(data);
    });
    try {
      if (typeof window.ClipboardItem === 'function' && navigator.clipboard?.write) {
        // Safari drops the click's permission across a fetch; handing the
        // clipboard a pending blob keeps the write inside the click.
        const blob = load.then((url) => new Blob([url], { type: 'text/plain' }));
        await navigator.clipboard.write([new window.ClipboardItem({ 'text/plain': blob })]);
        toast.success('Review link copied');
        return;
      }
      const url = await load;
      if (await copyText(url)) toast.success('Review link copied');
      else toast.error('Copy failed — select the text and copy it manually');
    } catch {
      let loaded = true;
      await load.catch((err) => { loaded = false; toast.error(err?.response ? errorMessage(err, 'Could not load the review link') : err.message); });
      if (loaded) toast.error('Copy failed — click Copy again');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-100" data-testid={`review-row-${review.review_id}`}>
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: '#eff6ff' }}>
          <Scale size={18} style={{ color: C.navy }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold" style={{ color: C.navy }}>{review.attorney_email}</span>
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ background: style.bg, color: style.fg }}>
              {style.label}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Sent {formatDate(review.created_at)} · {review.doc_count ?? full?.docs?.length ?? '—'} docs
            {review.decision_summary && ` · "${review.decision_summary}"`}
          </div>
        </div>
        <button onClick={copyLink} title="Copy review link" data-testid={`review-copy-${review.review_id}`}
                className="p-2 hover:bg-slate-100 rounded text-slate-500">
          <Copy size={14} />
        </button>
        {(review.status === 'approved' || review.status === 'changes_requested') && (
          <button
            onClick={() => downloadWithAuth(`/broker-kit/attorney/reviews/${review.review_id}/pdf`, 'attorney-review.pdf')}
            title="Download the record of this review"
            data-testid={`review-pdf-${review.review_id}`}
            className="text-xs px-3 h-8 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5">
            <Download size={13} /> PDF
          </button>
        )}
        <button onClick={loadFull} data-testid={`review-expand-${review.review_id}`}
                className="text-xs px-3 h-8 rounded-md border border-slate-200 hover:bg-slate-50">
          {expanded ? 'Hide' : 'View feedback'}
        </button>
      </div>
      {expanded && full && (
        <div className="border-t border-slate-100 p-4 bg-slate-50/50">
          {full.comments.length === 0 ? (
            <div className="text-xs text-slate-500">No comments yet.</div>
          ) : (
            <div className="space-y-2">
              {full.comments.map((c) => (
                <div key={c.id} className="bg-white rounded-lg p-3 border border-slate-100">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare size={12} className="text-slate-400" />
                    <span className="text-xs font-semibold" style={{ color: C.navy }}>{c.author_name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{
                      background: c.kind === 'change_request' ? '#fee2e2' : c.kind === 'approve_doc' ? '#dcfce7' : '#f1f5f9',
                      color: c.kind === 'change_request' ? '#991b1b' : c.kind === 'approve_doc' ? '#166534' : '#334155',
                    }}>{c.kind.replace('_', ' ')}</span>
                    {c.doc_id && <span className="text-[10px] text-slate-400 font-mono">{c.doc_id}</span>}
                    <span className="text-[10px] text-slate-400 ml-auto">{formatDateTime(c.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AttorneyModal({ settings, docs, onClose, onSent }) {
  const completedDocs = useMemo(() => docs.filter((d) => d.completed), [docs]);
  const [attorneyEmail, setAttorneyEmail] = useState(settings.attorney_email || '');
  const [attorneyName, setAttorneyName] = useState(settings.attorney_name || '');
  const [selectedIds, setSelectedIds] = useState(() => new Set(completedDocs.map((d) => d.id)));
  const [message, setMessage] = useState('');
  const [saveDefault, setSaveDefault] = useState(!settings.attorney_email);
  const [sending, setSending] = useState(false);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const send = async () => {
    if (!attorneyEmail) { toast.error('Attorney email required'); return; }
    if (selectedIds.size === 0) { toast.error('Select at least one document'); return; }
    setSending(true);
    try {
      if (saveDefault) {
        await api.post('/broker-kit/attorney/settings', {
          attorney_email: attorneyEmail,
          attorney_name: attorneyName,
        });
      }
      const { data } = await api.post('/broker-kit/attorney/send', {
        doc_ids: Array.from(selectedIds),
        attorney_email: attorneyEmail,
        attorney_name: attorneyName,
        message,
      });
      toast.success(data.delivered_via_email
        ? 'Email sent to attorney'
        : 'Review link created — copy from Inbox');
      onSent();
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast.error(errorMessage(e, 'Send failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        showClose={false}
        className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
        data-testid="attorney-modal"
      >
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Compliance review</div>
            <DialogPrimitive.Title asChild>
              <h3 className="font-serif text-lg font-semibold flex items-center gap-2" style={{ color: C.navy }}>
                <Scale size={16} /> Send docs to your attorney
              </h3>
            </DialogPrimitive.Title>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="attorney-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Attorney email *</label>
              <Input
                type="email"
                value={attorneyEmail}
                onChange={(e) => setAttorneyEmail(e.target.value)}
                placeholder="counsel@lawfirm.com"
                data-testid="attorney-email-input"
                className="h-10"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Attorney name</label>
              <Input
                type="text"
                value={attorneyName}
                onChange={(e) => setAttorneyName(e.target.value)}
                placeholder="Jane Counsel Esq."
                data-testid="attorney-name-input"
                className="h-10"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={saveDefault}
              onChange={(e) => setSaveDefault(e.target.checked)}
              data-testid="attorney-save-default"
            />
            Save as default compliance counsel for this brokerage
          </label>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Note to attorney (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              placeholder="e.g. Please review before Q1 audit."
              data-testid="attorney-message"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6BA82E]/40"
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
                Documents to include ({selectedIds.size} selected)
              </label>
              {completedDocs.length === 0 && (
                <span className="text-[11px] text-amber-600">Tip: mark docs complete to auto-select them.</span>
              )}
            </div>
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-52 overflow-y-auto">
              {docs.map((d) => (
                <label key={d.id} className="flex items-start gap-2 p-2.5 hover:bg-slate-50 cursor-pointer" data-testid={`attorney-doc-${d.id}`}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(d.id)}
                    onChange={() => toggle(d.id)}
                    className="mt-0.5"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: C.navy }}>{d.title}</div>
                    <div className="text-[11px] text-slate-400 font-mono">{d.category} · {d.completed ? 'complete' : 'draft'}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
          <button onClick={onClose} className="text-sm px-4 h-9 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button
            onClick={send}
            disabled={sending || !attorneyEmail || selectedIds.size === 0}
            data-testid="attorney-send"
            className="text-sm px-4 h-9 rounded-md text-white font-medium inline-flex items-center gap-2 disabled:opacity-60"
            style={{ background: C.green }}
          >
            {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            Send for review
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

